/**
 * High-level integration between dynamic-block entities and the
 * low-level constraint solver.
 *
 * This module:
 *   1. Walks a list of BlockChildEntity-shaped objects and emits one
 *      ConstraintVariable per geometric degree-of-freedom (line
 *      endpoints, circle center+radius, arc center+radius+angles).
 *   2. Accepts a list of GeometricConstraint and DimensionalConstraint
 *      descriptors (the DYN-3 Batch A schema work; typed inline here
 *      as a placeholder with the same wire shape expected of the
 *      eventual schemas).
 *   3. Builds ConstraintResidual instances using the builders from
 *      constraintResiduals.ts.
 *   4. Runs the solver.
 *   5. Writes solved values back into fresh entity copies.
 *
 * Design:
 *   - Variable id format is `<entityId>.<field>` so every unknown is
 *     globally unique. For a Line 'L1' this produces
 *     'L1.startX', 'L1.startY', 'L1.endX', 'L1.endY'.
 *   - Circle 'C1' → 'C1.cx', 'C1.cy', 'C1.r'.
 *   - Arc   'A1' → 'A1.cx', 'A1.cy', 'A1.r', 'A1.startAngle', 'A1.endAngle'.
 *   - LWPOLYLINE vertices: 'P1.v<i>.x', 'P1.v<i>.y'.
 *   - Generic Point entities (future DYN-3 POINT primitive): 'PT.x', 'PT.y'.
 *
 * Fixed entities (Fix geometric constraint referencing an entire entity):
 *   — every variable derived from that entity gets `fixed: true`.
 *
 * Fixed points (Fix constraint on a single point ref):
 *   — only the two coordinate variables of that point get `fixed: true`.
 *
 * Constraint refs in the high-level descriptor use
 * `{ entityId, part: "start" | "end" | "center" | "vertex#k" }` — we
 * map these to the low-level PointRef/LineRef/CircleRef/ArcRef required
 * by the residual builders.
 */

import type {
  ConstraintResidual,
  ConstraintVariable,
  SolveResult,
} from "./constraintSolver.types";
import type { PointRef, LineRef, CircleRef } from "./constraintSolver.types";
import { solveConstraints } from "./constraintSolver";
import {
  alignedDistanceResidual,
  angularConstraintResidual,
  coincidentResidual,
  collinearResidual,
  concentricResidual,
  diameterResidual,
  equalLengthResidual,
  equalValueResidual,
  horizontalDistanceResidual,
  horizontalResidual,
  parallelResidual,
  perpendicularResidual,
  radialResidual,
  symmetricResidual,
  tangentResidual,
  verticalDistanceResidual,
  verticalResidual,
} from "./constraintResiduals";
import type { EntityLike } from "./geometry";

// ═══════════════════════════════════════════════════════════════════════
// Geometric constraint descriptor — matches the DYN-3 Batch A schema
// (dynamicBlockConstraints.ts):
//
//   { id, kind, entityIds[], entitySubselectors?[], referenceLineId? }
//
// We accept that shape directly so no translation layer is needed at the
// evaluator boundary. The resolution functions below map
// (entityId, subselector) → PointRef / LineRef / CircleRef.
// ═══════════════════════════════════════════════════════════════════════

export type GeometricConstraintKind =
  | "coincident"
  | "collinear"
  | "parallel"
  | "perpendicular"
  | "horizontal"
  | "vertical"
  | "tangent"
  | "equal"
  | "symmetric"
  | "concentric"
  | "fix"
  | "smooth";

export interface GeometricConstraint {
  readonly id: string;
  readonly kind: GeometricConstraintKind;
  /**
   * Ordered list of block-entity ids this constraint operates on.
   * Positional semantics depend on kind (e.g. for "tangent" entityIds[0]
   * is a circle/arc, entityIds[1] is a line).
   */
  readonly entityIds: readonly string[];
  /**
   * Parallel array (same length as entityIds) that picks a subfeature of
   * each entity: "start" | "end" | "center" | "v<n>" for polyline
   * vertices, or "" / undefined to mean "the entity as a whole".
   */
  readonly entitySubselectors?: readonly string[];
  /** For "symmetric": entity id of the mirror-axis line. */
  readonly referenceLineId?: string;
}

/**
 * Dimensional constraint wire shape — DYN-2 dimensional constraint
 * parameters expose a numeric `currentValue` (distance or angle); here we
 * consume that same payload alongside entity references to participate
 * in the solver.
 */
export type DimensionalConstraintKind =
  | "horizontal-distance"
  | "vertical-distance"
  | "aligned-distance"
  | "angular-distance"
  | "radial"
  | "diameter";

export interface DimensionalConstraint {
  readonly id: string;
  readonly kind: DimensionalConstraintKind;
  /** Target numeric value (radians for angles, length for distances). */
  readonly currentValue: number;
  /** Entity ids involved. See kind-specific notes below. */
  readonly entityIds: readonly string[];
  /** Parallel-to-entityIds subselector array (same semantics as geometric). */
  readonly entitySubselectors?: readonly string[];
}

// ═══════════════════════════════════════════════════════════════════════
// Variable extraction from entities.
// ═══════════════════════════════════════════════════════════════════════

interface EntityVars {
  readonly entityId: string;
  readonly varIds: readonly string[]; // all vars derived from this entity
}

interface VarExtraction {
  readonly variables: ConstraintVariable[];
  readonly entityIndex: Map<string, EntityVars>;
  readonly nonFixedIds: Set<string>;
}

function extractVariables(
  entities: readonly (EntityLike & { readonly id: string })[],
): VarExtraction {
  const variables: ConstraintVariable[] = [];
  const entityIndex = new Map<string, EntityVars>();
  const nonFixedIds = new Set<string>();

  for (const e of entities) {
    const ids: string[] = [];
    switch (e.type) {
      case "Line": {
        const line = e as unknown as {
          start: { x: number; y: number };
          end: { x: number; y: number };
        };
        const names = [
          `${e.id}.startX`,
          `${e.id}.startY`,
          `${e.id}.endX`,
          `${e.id}.endY`,
        ];
        const vals = [line.start.x, line.start.y, line.end.x, line.end.y];
        for (let i = 0; i < 4; i++) {
          variables.push({ id: names[i]!, initialValue: vals[i]! });
          ids.push(names[i]!);
          nonFixedIds.add(names[i]!);
        }
        break;
      }
      case "Circle": {
        const c = e as unknown as {
          center: { x: number; y: number };
          radius: number;
        };
        const names = [`${e.id}.cx`, `${e.id}.cy`, `${e.id}.r`];
        const vals = [c.center.x, c.center.y, c.radius];
        for (let i = 0; i < 3; i++) {
          variables.push({ id: names[i]!, initialValue: vals[i]! });
          ids.push(names[i]!);
          nonFixedIds.add(names[i]!);
        }
        break;
      }
      case "Arc": {
        const a = e as unknown as {
          center: { x: number; y: number };
          radius: number;
          startAngle: number;
          endAngle: number;
        };
        const names = [
          `${e.id}.cx`,
          `${e.id}.cy`,
          `${e.id}.r`,
          `${e.id}.startAngle`,
          `${e.id}.endAngle`,
        ];
        const vals = [
          a.center.x,
          a.center.y,
          a.radius,
          a.startAngle,
          a.endAngle,
        ];
        for (let i = 0; i < 5; i++) {
          variables.push({ id: names[i]!, initialValue: vals[i]! });
          ids.push(names[i]!);
          nonFixedIds.add(names[i]!);
        }
        break;
      }
      case "LWPOLYLINE": {
        const p = e as unknown as {
          vertices: ReadonlyArray<{ x: number; y: number }>;
        };
        for (let vi = 0; vi < p.vertices.length; vi++) {
          const vx = `${e.id}.v${vi}.x`;
          const vy = `${e.id}.v${vi}.y`;
          variables.push({ id: vx, initialValue: p.vertices[vi]!.x });
          variables.push({ id: vy, initialValue: p.vertices[vi]!.y });
          ids.push(vx, vy);
          nonFixedIds.add(vx);
          nonFixedIds.add(vy);
        }
        break;
      }
      default:
        // Text / Block / Attdef / etc — not yet participating in the
        // constraint solver (AutoCAD's constraints operate on the listed
        // geometric primitives).
        break;
    }
    entityIndex.set(e.id, { entityId: e.id, varIds: ids });
  }

  return { variables, entityIndex, nonFixedIds };
}

// ═══════════════════════════════════════════════════════════════════════
// Reference resolution — (entityId, subselector) → low-level variable-id
// refs used by the residual builders.
// ═══════════════════════════════════════════════════════════════════════

/**
 * Parse a "v<n>" polyline-vertex subselector, returning the numeric index
 * or undefined when the string does not match.
 */
function parseVertexIndex(sub: string): number | undefined {
  if (!sub.startsWith("v")) return undefined;
  const n = Number(sub.slice(1));
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : undefined;
}

function findEntity(
  entityId: string,
  entities: readonly (EntityLike & { readonly id: string })[],
): (EntityLike & { readonly id: string }) | undefined {
  return entities.find((e) => e.id === entityId);
}

/**
 * Resolve a PointRef from an (entityId, subselector). Subselector may be
 * "start" | "end" | "center" | "v<n>" (polyline vertex). Empty/undefined
 * defaults to the entity's canonical anchor (Line.start, Circle.center,
 * Arc.center, polyline.v0).
 */
function resolvePointRef(
  entityId: string,
  subselector: string | undefined,
  entities: readonly (EntityLike & { readonly id: string })[],
  warnings: string[],
): PointRef | null {
  const ent = findEntity(entityId, entities);
  if (!ent) {
    warnings.push(`Constraint references missing entity ${entityId}`);
    return null;
  }
  const sub = (subselector ?? "").trim();
  if (ent.type === "Line") {
    if (sub === "end") {
      return { x: `${entityId}.endX`, y: `${entityId}.endY` };
    }
    // Default / "start".
    return { x: `${entityId}.startX`, y: `${entityId}.startY` };
  }
  if (ent.type === "Circle" || ent.type === "Arc") {
    // Circle / Arc expose only the center as a point ref.
    return { x: `${entityId}.cx`, y: `${entityId}.cy` };
  }
  if (ent.type === "LWPOLYLINE") {
    const idx = sub ? parseVertexIndex(sub) : 0;
    if (idx === undefined) {
      warnings.push(
        `Polyline ${entityId} subselector "${sub}" not recognized (expected "v<n>")`,
      );
      return null;
    }
    return { x: `${entityId}.v${idx}.x`, y: `${entityId}.v${idx}.y` };
  }
  warnings.push(
    `Cannot resolve point on ${ent.type}#${entityId} (subselector="${sub}")`,
  );
  return null;
}

/**
 * Resolve a LineRef. Supports real Line entities (default — whole line)
 * and polyline segments via subselector "v<i>-v<j>".
 */
function resolveLineRef(
  entityId: string,
  subselector: string | undefined,
  entities: readonly (EntityLike & { readonly id: string })[],
  warnings: string[],
): LineRef | null {
  const ent = findEntity(entityId, entities);
  if (!ent) {
    warnings.push(`Constraint references missing entity ${entityId}`);
    return null;
  }
  if (ent.type === "Line") {
    return {
      startX: `${entityId}.startX`,
      startY: `${entityId}.startY`,
      endX: `${entityId}.endX`,
      endY: `${entityId}.endY`,
    };
  }
  if (ent.type === "LWPOLYLINE" && subselector) {
    // Expected subselector form "v<i>-v<j>".
    const m = /^v(\d+)-v(\d+)$/.exec(subselector);
    if (m) {
      const i = Number(m[1]);
      const j = Number(m[2]);
      return {
        startX: `${entityId}.v${i}.x`,
        startY: `${entityId}.v${i}.y`,
        endX: `${entityId}.v${j}.x`,
        endY: `${entityId}.v${j}.y`,
      };
    }
  }
  warnings.push(
    `Cannot resolve line on ${ent.type}#${entityId} (subselector="${subselector ?? ""}")`,
  );
  return null;
}

function resolveCircleRef(
  entityId: string,
  entities: readonly (EntityLike & { readonly id: string })[],
  warnings: string[],
): CircleRef | null {
  const ent = findEntity(entityId, entities);
  if (!ent) {
    warnings.push(`Constraint references missing entity ${entityId}`);
    return null;
  }
  if (ent.type === "Circle" || ent.type === "Arc") {
    return {
      centerX: `${entityId}.cx`,
      centerY: `${entityId}.cy`,
      radius: `${entityId}.r`,
    };
  }
  warnings.push(`Circle ref on ${ent.type}#${entityId} — not a circle`);
  return null;
}

/**
 * Convenience: entityIds[i], entitySubselectors?[i] → point ref.
 */
function pointAt(
  c: GeometricConstraint | DimensionalConstraint,
  i: number,
  entities: readonly (EntityLike & { readonly id: string })[],
  warnings: string[],
): PointRef | null {
  const eid = c.entityIds[i];
  if (!eid) return null;
  return resolvePointRef(eid, c.entitySubselectors?.[i], entities, warnings);
}

function lineAt(
  c: GeometricConstraint | DimensionalConstraint,
  i: number,
  entities: readonly (EntityLike & { readonly id: string })[],
  warnings: string[],
): LineRef | null {
  const eid = c.entityIds[i];
  if (!eid) return null;
  return resolveLineRef(eid, c.entitySubselectors?.[i], entities, warnings);
}

function circleAt(
  c: GeometricConstraint | DimensionalConstraint,
  i: number,
  entities: readonly (EntityLike & { readonly id: string })[],
  warnings: string[],
): CircleRef | null {
  const eid = c.entityIds[i];
  if (!eid) return null;
  return resolveCircleRef(eid, entities, warnings);
}

// ═══════════════════════════════════════════════════════════════════════
// Residual assembly from geometric / dimensional constraint descriptors.
// ═══════════════════════════════════════════════════════════════════════

function buildGeometricResiduals(
  constraints: readonly GeometricConstraint[],
  entities: readonly (EntityLike & { readonly id: string })[],
  fixVariables: Set<string>,
  warnings: string[],
): ConstraintResidual[] {
  const out: ConstraintResidual[] = [];
  for (const c of constraints) {
    switch (c.kind) {
      case "fix": {
        // Fix kind: mark every variable derived from each referenced
        // entity as fixed. If a subselector narrows to a single point
        // (start/end/center/v<n>) only that pair is fixed; otherwise
        // the entire entity is fixed.
        for (let i = 0; i < c.entityIds.length; i++) {
          const eid = c.entityIds[i]!;
          const sub = c.entitySubselectors?.[i];
          const ent = findEntity(eid, entities);
          if (!ent) {
            warnings.push(`Fix constraint references missing entity ${eid}`);
            continue;
          }
          if (sub && sub.length > 0) {
            const pr = resolvePointRef(eid, sub, entities, warnings);
            if (pr) {
              fixVariables.add(pr.x);
              fixVariables.add(pr.y);
            }
          } else {
            for (const v of allVarIdsForEntity(ent)) fixVariables.add(v);
          }
        }
        break;
      }
      case "coincident": {
        if (c.entityIds.length < 2) {
          warnings.push(`coincident requires ≥2 entities`);
          break;
        }
        // Chain coincident — entity[0] ≡ entity[1], entity[1] ≡ entity[2]...
        for (let i = 0; i < c.entityIds.length - 1; i++) {
          const a = pointAt(c, i, entities, warnings);
          const b = pointAt(c, i + 1, entities, warnings);
          if (a && b) out.push(...coincidentResidual(a, b));
        }
        break;
      }
      case "collinear": {
        if (c.entityIds.length < 2) {
          warnings.push(`collinear requires ≥2 lines`);
          break;
        }
        // For each consecutive triple of line endpoints, enforce
        // collinearity of (startA, endA, endB). This keeps all line
        // endpoints on a single axis.
        const anchor = lineAt(c, 0, entities, warnings);
        if (!anchor) break;
        const anchorS: PointRef = { x: anchor.startX, y: anchor.startY };
        const anchorE: PointRef = { x: anchor.endX, y: anchor.endY };
        for (let i = 1; i < c.entityIds.length; i++) {
          const next = lineAt(c, i, entities, warnings);
          if (!next) continue;
          const nS: PointRef = { x: next.startX, y: next.startY };
          const nE: PointRef = { x: next.endX, y: next.endY };
          out.push(collinearResidual(anchorS, anchorE, nS));
          out.push(collinearResidual(anchorS, anchorE, nE));
        }
        break;
      }
      case "parallel": {
        if (c.entityIds.length !== 2) {
          warnings.push(`parallel requires exactly 2 lines`);
          break;
        }
        const l1 = lineAt(c, 0, entities, warnings);
        const l2 = lineAt(c, 1, entities, warnings);
        if (l1 && l2) out.push(parallelResidual(l1, l2));
        break;
      }
      case "perpendicular": {
        if (c.entityIds.length !== 2) {
          warnings.push(`perpendicular requires exactly 2 lines`);
          break;
        }
        const l1 = lineAt(c, 0, entities, warnings);
        const l2 = lineAt(c, 1, entities, warnings);
        if (l1 && l2) out.push(perpendicularResidual(l1, l2));
        break;
      }
      case "horizontal": {
        if (c.entityIds.length !== 1) {
          warnings.push(`horizontal requires exactly 1 line`);
          break;
        }
        const l = lineAt(c, 0, entities, warnings);
        if (l) out.push(horizontalResidual(l));
        break;
      }
      case "vertical": {
        if (c.entityIds.length !== 1) {
          warnings.push(`vertical requires exactly 1 line`);
          break;
        }
        const l = lineAt(c, 0, entities, warnings);
        if (l) out.push(verticalResidual(l));
        break;
      }
      case "tangent": {
        if (c.entityIds.length !== 2) {
          warnings.push(`tangent requires exactly 2 entities`);
          break;
        }
        // Classify first entity: circle/arc or line.
        const e0 = findEntity(c.entityIds[0]!, entities);
        const e1 = findEntity(c.entityIds[1]!, entities);
        if (!e0 || !e1) {
          warnings.push(`tangent references missing entity`);
          break;
        }
        const e0IsCircle = e0.type === "Circle" || e0.type === "Arc";
        const e1IsCircle = e1.type === "Circle" || e1.type === "Arc";
        if (e0IsCircle && !e1IsCircle) {
          const circle = circleAt(c, 0, entities, warnings);
          const line = lineAt(c, 1, entities, warnings);
          if (circle && line) out.push(tangentResidual(circle, line));
        } else if (!e0IsCircle && e1IsCircle) {
          const line = lineAt(c, 0, entities, warnings);
          const circle = circleAt(c, 1, entities, warnings);
          if (circle && line) out.push(tangentResidual(circle, line));
        } else {
          warnings.push(
            `tangent currently supports circle/arc ↔ line only (got ${e0.type}+${e1.type})`,
          );
        }
        break;
      }
      case "equal": {
        if (c.entityIds.length !== 2) {
          warnings.push(`equal requires exactly 2 entities`);
          break;
        }
        const e0 = findEntity(c.entityIds[0]!, entities);
        const e1 = findEntity(c.entityIds[1]!, entities);
        if (!e0 || !e1) {
          warnings.push(`equal references missing entity`);
          break;
        }
        if (e0.type === "Line" && e1.type === "Line") {
          const l1 = lineAt(c, 0, entities, warnings);
          const l2 = lineAt(c, 1, entities, warnings);
          if (l1 && l2) out.push(equalLengthResidual(l1, l2));
        } else if (
          (e0.type === "Circle" || e0.type === "Arc") &&
          (e1.type === "Circle" || e1.type === "Arc")
        ) {
          const c1 = circleAt(c, 0, entities, warnings);
          const c2 = circleAt(c, 1, entities, warnings);
          if (c1 && c2) out.push(equalValueResidual(c1.radius, c2.radius));
        } else {
          warnings.push(
            `equal requires two lines OR two circles (got ${e0.type}+${e1.type})`,
          );
        }
        break;
      }
      case "symmetric": {
        if (c.entityIds.length !== 2 || !c.referenceLineId) {
          warnings.push(
            `symmetric requires exactly 2 entities + referenceLineId`,
          );
          break;
        }
        const a = pointAt(c, 0, entities, warnings);
        const b = pointAt(c, 1, entities, warnings);
        const axis = resolveLineRef(
          c.referenceLineId,
          undefined,
          entities,
          warnings,
        );
        if (a && b && axis) {
          const axisStart: PointRef = { x: axis.startX, y: axis.startY };
          const axisEnd: PointRef = { x: axis.endX, y: axis.endY };
          out.push(...symmetricResidual(a, b, axisStart, axisEnd));
        }
        break;
      }
      case "concentric": {
        if (c.entityIds.length < 2) {
          warnings.push(`concentric requires ≥2 circles`);
          break;
        }
        const anchor = circleAt(c, 0, entities, warnings);
        if (!anchor) break;
        for (let i = 1; i < c.entityIds.length; i++) {
          const next = circleAt(c, i, entities, warnings);
          if (next) out.push(...concentricResidual(anchor, next));
        }
        break;
      }
      case "smooth": {
        if (c.entityIds.length !== 2) {
          warnings.push(`smooth requires exactly 2 curves`);
          break;
        }
        // G1 continuity: end of curve1 coincides with start of curve2
        // (via their "end" / "start" subselectors) and the tangent
        // vectors at the seam are parallel. For lines the tangent vector
        // is the line itself — treat each Line directly.
        const l1 = lineAt(c, 0, entities, warnings);
        const l2 = lineAt(c, 1, entities, warnings);
        if (!l1 || !l2) break;
        const l1End: PointRef = { x: l1.endX, y: l1.endY };
        const l2Start: PointRef = { x: l2.startX, y: l2.startY };
        out.push(...coincidentResidual(l1End, l2Start));
        out.push(parallelResidual(l1, l2));
        break;
      }
    }
  }
  return out;
}

function buildDimensionalResiduals(
  constraints: readonly DimensionalConstraint[],
  entities: readonly (EntityLike & { readonly id: string })[],
  warnings: string[],
): ConstraintResidual[] {
  const out: ConstraintResidual[] = [];
  for (const c of constraints) {
    switch (c.kind) {
      case "horizontal-distance":
      case "vertical-distance":
      case "aligned-distance": {
        if (c.entityIds.length !== 2) {
          warnings.push(`${c.kind} requires 2 point refs`);
          break;
        }
        const a = pointAt(c, 0, entities, warnings);
        const b = pointAt(c, 1, entities, warnings);
        if (a && b) {
          if (c.kind === "horizontal-distance") {
            out.push(horizontalDistanceResidual(a, b, c.currentValue));
          } else if (c.kind === "vertical-distance") {
            out.push(verticalDistanceResidual(a, b, c.currentValue));
          } else {
            out.push(alignedDistanceResidual(a, b, c.currentValue));
          }
        }
        break;
      }
      case "angular-distance": {
        if (c.entityIds.length !== 3) {
          warnings.push(`angular-distance requires 3 point refs`);
          break;
        }
        const vertex = pointAt(c, 0, entities, warnings);
        const a = pointAt(c, 1, entities, warnings);
        const b = pointAt(c, 2, entities, warnings);
        if (vertex && a && b) {
          out.push(angularConstraintResidual(vertex, a, b, c.currentValue));
        }
        break;
      }
      case "radial": {
        if (c.entityIds.length !== 1) {
          warnings.push(`radial requires 1 circle/arc`);
          break;
        }
        const circle = circleAt(c, 0, entities, warnings);
        if (circle) out.push(radialResidual(circle, c.currentValue));
        break;
      }
      case "diameter": {
        if (c.entityIds.length !== 1) {
          warnings.push(`diameter requires 1 circle/arc`);
          break;
        }
        const circle = circleAt(c, 0, entities, warnings);
        if (circle) out.push(diameterResidual(circle, c.currentValue));
        break;
      }
    }
  }
  return out;
}

function allVarIdsForEntity(
  entity: EntityLike & { readonly id: string },
): string[] {
  switch (entity.type) {
    case "Line":
      return [
        `${entity.id}.startX`,
        `${entity.id}.startY`,
        `${entity.id}.endX`,
        `${entity.id}.endY`,
      ];
    case "Circle":
      return [`${entity.id}.cx`, `${entity.id}.cy`, `${entity.id}.r`];
    case "Arc":
      return [
        `${entity.id}.cx`,
        `${entity.id}.cy`,
        `${entity.id}.r`,
        `${entity.id}.startAngle`,
        `${entity.id}.endAngle`,
      ];
    case "LWPOLYLINE": {
      const p = entity as unknown as {
        vertices: ReadonlyArray<{ x: number; y: number }>;
      };
      const out: string[] = [];
      for (let i = 0; i < p.vertices.length; i++) {
        out.push(`${entity.id}.v${i}.x`, `${entity.id}.v${i}.y`);
      }
      return out;
    }
    default:
      return [];
  }
}

// ═══════════════════════════════════════════════════════════════════════
// Write-back — solved variable values back into entity copies.
// ═══════════════════════════════════════════════════════════════════════

function writeBackEntities(
  entities: readonly (EntityLike & { readonly id: string })[],
  solved: Readonly<Record<string, number>>,
): Array<EntityLike & { id: string }> {
  return entities.map((e) => {
    switch (e.type) {
      case "Line": {
        const sx = solved[`${e.id}.startX`];
        const sy = solved[`${e.id}.startY`];
        const ex = solved[`${e.id}.endX`];
        const ey = solved[`${e.id}.endY`];
        if (
          sx === undefined ||
          sy === undefined ||
          ex === undefined ||
          ey === undefined
        ) {
          return e as EntityLike & { id: string };
        }
        return {
          ...e,
          start: { x: sx, y: sy },
          end: { x: ex, y: ey },
        } as EntityLike & { id: string };
      }
      case "Circle": {
        const cx = solved[`${e.id}.cx`];
        const cy = solved[`${e.id}.cy`];
        const r = solved[`${e.id}.r`];
        if (cx === undefined || cy === undefined || r === undefined) {
          return e as EntityLike & { id: string };
        }
        return {
          ...e,
          center: { x: cx, y: cy },
          radius: r,
        } as EntityLike & { id: string };
      }
      case "Arc": {
        const cx = solved[`${e.id}.cx`];
        const cy = solved[`${e.id}.cy`];
        const r = solved[`${e.id}.r`];
        const sa = solved[`${e.id}.startAngle`];
        const ea = solved[`${e.id}.endAngle`];
        if (cx === undefined || cy === undefined || r === undefined) {
          return e as EntityLike & { id: string };
        }
        return {
          ...e,
          center: { x: cx, y: cy },
          radius: r,
          startAngle: sa ?? (e as unknown as { startAngle: number }).startAngle,
          endAngle: ea ?? (e as unknown as { endAngle: number }).endAngle,
        } as EntityLike & { id: string };
      }
      case "LWPOLYLINE": {
        const p = e as unknown as {
          vertices: ReadonlyArray<
            { x: number; y: number } & Record<string, unknown>
          >;
        };
        const nextVerts = p.vertices.map((v, i) => {
          const vx = solved[`${e.id}.v${i}.x`];
          const vy = solved[`${e.id}.v${i}.y`];
          return vx !== undefined && vy !== undefined
            ? { ...v, x: vx, y: vy }
            : v;
        });
        return { ...e, vertices: nextVerts } as EntityLike & { id: string };
      }
      default:
        return e as EntityLike & { id: string };
    }
  });
}

// ═══════════════════════════════════════════════════════════════════════
// Public API
// ═══════════════════════════════════════════════════════════════════════

export interface SolveBlockConstraintsInput {
  readonly entities: readonly (EntityLike & { readonly id: string })[];
  readonly geometricConstraints?: readonly GeometricConstraint[];
  readonly dimensionalConstraints?: readonly DimensionalConstraint[];
  /** Additional entity ids to fully fix (on top of any Fix geometric constraint). */
  readonly fixedEntityIds?: readonly string[];
  /** Pass-through solver options. */
  readonly solverOptions?: SolveResult["errors"] extends undefined
    ? never
    : never;
  /**
   * Optional block name for warm-start cache keying (DYN-4.3). Supplying
   * a stable identifier across successive invocations allows the solver
   * to reuse the previous solution as the initial guess whenever the
   * variable set remains structurally compatible.
   */
  readonly blockName?: string;
}

export interface SolveBlockConstraintsResult {
  readonly updatedEntities: Array<EntityLike & { id: string }>;
  readonly success: boolean;
  readonly iterations: number;
  readonly residualNorm: number;
  readonly warnings: readonly string[];
  /**
   * DYN-4.3: `false` when the block name was supplied but no matching
   * cache entry was found (or the cached variable set did not match).
   * `true` when a cached initial guess was reused. Undefined for
   * unnamed calls.
   */
  readonly cacheHit?: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// Warm-start cache (DYN-4.3)
// ═══════════════════════════════════════════════════════════════════
//
// Keyed by blockName. Stores the last successful solution's variable
// map. When the next call presents the same block name AND the same
// variable set (same ids, same fixed flags), the cached values
// pre-seed `initialValue` — Gauss-Newton / LM converges in a handful of
// iterations for small parametric changes.
//
// Bounded by WARM_CACHE_LIMIT most-recently-used entries.

interface WarmEntry {
  readonly variableIds: readonly string[];
  readonly values: Readonly<Record<string, number>>;
  readonly fixedKey: string;
}

const WARM_CACHE_LIMIT = 64;
const warmCache = new Map<string, WarmEntry>();

function remember(
  blockName: string,
  variables: readonly ConstraintVariable[],
  values: Readonly<Record<string, number>>,
): void {
  const ids = variables
    .map((v) => v.id)
    .slice()
    .sort();
  const fixedKey = variables
    .filter((v) => v.fixed)
    .map((v) => v.id)
    .sort()
    .join("|");
  // Extract only the values we know about; drop anything else to keep
  // the entry compact.
  const kept: Record<string, number> = {};
  for (const id of ids) kept[id] = values[id] ?? 0;
  warmCache.set(blockName, { variableIds: ids, values: kept, fixedKey });
  // Bounded MRU — prune oldest.
  if (warmCache.size > WARM_CACHE_LIMIT) {
    const firstKey = warmCache.keys().next().value;
    if (firstKey !== undefined) warmCache.delete(firstKey);
  }
}

function lookupWarm(
  blockName: string,
  variables: readonly ConstraintVariable[],
): Readonly<Record<string, number>> | undefined {
  const entry = warmCache.get(blockName);
  if (!entry) return undefined;
  const ids = variables
    .map((v) => v.id)
    .slice()
    .sort();
  if (ids.length !== entry.variableIds.length) return undefined;
  for (let i = 0; i < ids.length; i++) {
    if (ids[i] !== entry.variableIds[i]) return undefined;
  }
  const fixedKey = variables
    .filter((v) => v.fixed)
    .map((v) => v.id)
    .sort()
    .join("|");
  if (fixedKey !== entry.fixedKey) return undefined;
  return entry.values;
}

/** Clear the warm-start cache — used by tests and block-schema changes. */
export function clearSolverWarmCache(): void {
  warmCache.clear();
}

/**
 * Solve the constraint system for a block's entities. Returns transformed
 * entities (or the originals unchanged on failure) plus diagnostics.
 *
 * Semantics:
 *   - If no constraints: returns entities unchanged, success=true.
 *   - On solver failure: entities keep their pre-solve geometry;
 *     `success=false` and the final residualNorm are reported so the
 *     caller (evaluator) can decide whether to display a warning.
 */
export function solveBlockConstraints(
  input: SolveBlockConstraintsInput,
): SolveBlockConstraintsResult {
  const warnings: string[] = [];
  const geometrics = input.geometricConstraints ?? [];
  const dimensionals = input.dimensionalConstraints ?? [];

  if (geometrics.length === 0 && dimensionals.length === 0) {
    return {
      updatedEntities: input.entities.map(
        (e) => e as EntityLike & { id: string },
      ),
      success: true,
      iterations: 0,
      residualNorm: 0,
      warnings,
    };
  }

  const { variables, nonFixedIds } = extractVariables(input.entities);

  const fixVars = new Set<string>();
  if (input.fixedEntityIds) {
    for (const eid of input.fixedEntityIds) {
      const ent = input.entities.find((e) => e.id === eid);
      if (ent) {
        for (const v of allVarIdsForEntity(ent)) fixVars.add(v);
      }
    }
  }

  // DYN-4.3: symbolic simplification — drop tautological constraints
  // (same entity passed twice), deduplicate identical constraint tuples.
  const simplifiedGeo = simplifyGeometricConstraints(geometrics);

  const residuals = [
    ...buildGeometricResiduals(
      simplifiedGeo,
      input.entities,
      fixVars,
      warnings,
    ),
    ...buildDimensionalResiduals(dimensionals, input.entities, warnings),
  ];

  // Apply fixed flag.
  let solverVars: ConstraintVariable[] = variables.map((v) =>
    fixVars.has(v.id) ? { ...v, fixed: true } : v,
  );
  void nonFixedIds;

  // DYN-4.3: warm-start. When the caller supplies a blockName we look
  // up the last successful solution's variable values and use them as
  // the initial guess for every non-fixed variable. Speeds convergence
  // dramatically on repeated grip-drag updates.
  let cacheHit: boolean | undefined;
  if (input.blockName !== undefined) {
    const warm = lookupWarm(input.blockName, solverVars);
    if (warm !== undefined) {
      solverVars = solverVars.map((v) =>
        v.fixed || warm[v.id] === undefined
          ? v
          : { ...v, initialValue: warm[v.id]! },
      );
      cacheHit = true;
    } else {
      cacheHit = false;
    }
  }

  // Empty residual list (could happen if all constraints were Fix only —
  // nothing to solve). Return with success and unchanged entities.
  if (residuals.length === 0) {
    return {
      updatedEntities: input.entities.map(
        (e) => e as EntityLike & { id: string },
      ),
      success: true,
      iterations: 0,
      residualNorm: 0,
      warnings,
      ...(cacheHit !== undefined ? { cacheHit } : {}),
    };
  }

  const result = solveConstraints({
    variables: solverVars,
    residuals,
  });

  if (result.errors && result.errors.length > 0) {
    warnings.push(...result.errors);
  }

  // On failure, keep original geometry (don't half-apply a partial solve).
  const updatedEntities = result.success
    ? writeBackEntities(input.entities, result.variables)
    : input.entities.map((e) => e as EntityLike & { id: string });

  // Remember only successful solutions — warm-start from a diverged
  // state would poison the next iteration.
  if (result.success && input.blockName !== undefined) {
    remember(input.blockName, solverVars, result.variables);
  }

  return {
    updatedEntities,
    success: result.success,
    iterations: result.iterations,
    residualNorm: result.residualNorm,
    warnings,
    ...(cacheHit !== undefined ? { cacheHit } : {}),
  };
}

/**
 * Simplify a list of geometric constraints by:
 *   - dropping tautologies (Parallel(A,A), Coincident(A,A), …);
 *   - deduplicating identical constraints authored twice.
 *
 * Returns a new array; original order is preserved for the survivors.
 */
function simplifyGeometricConstraints(
  constraints: readonly GeometricConstraint[],
): GeometricConstraint[] {
  const seen = new Set<string>();
  const kept: GeometricConstraint[] = [];
  for (const c of constraints) {
    // Skip n-ary constraints where all references point at the same
    // entity — e.g. Parallel(A, A), Concentric(A, A).
    if (c.entityIds.length >= 2) {
      const first = c.entityIds[0]!;
      let allSame = true;
      for (let i = 1; i < c.entityIds.length; i++) {
        if (c.entityIds[i] !== first) {
          allSame = false;
          break;
        }
      }
      if (allSame) continue;
    }
    // Dedup key — kind + sorted entity ids + sorted subselector ids.
    const key = [
      c.kind,
      c.entityIds.slice().sort().join(","),
      (c.entitySubselectors ?? []).join("|"),
    ].join("#");
    if (seen.has(key)) continue;
    seen.add(key);
    kept.push(c);
  }
  return kept;
}
